import React, { useEffect, useRef, useState } from 'react';
import { collection, deleteDoc as nativeDeleteDoc, doc, getDocs as nativeGetDocs, serverTimestamp, setDoc as nativeSetDoc } from 'firebase/firestore';
import { deleteObject as nativeDeleteObject, getDownloadURL as nativeGetDownloadURL, ref, uploadBytes as nativeUploadBytes } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, functions, storage } from '../../../firebase-client';
import SubmitButton from '../formComponents/SubmitButton';
import ErrorMessage from '../formComponents/ErrorMessage';
import {
  DOCUMENT_CATEGORIES,
  DOCUMENT_VISIBILITIES,
  normalizeTeamDocuments,
} from '../../teams/utils/documentUtils';

function createEmptyDocument(sortOrder = 0, association = null) {
  return {
    id: crypto.randomUUID(),
    title: '',
    description: '',
    sourceType: 'external',
    fileUrl: '',
    externalUrl: '',
    fileName: '',
    storagePath: '',
    category: 'reference',
    visibility: 'public',
    linkedEventIds: association?.sourceType === 'event' && association.sourceId ? [association.sourceId] : [],
    linkedCourseIds: association?.sourceType === 'course' && association.sourceId ? [association.sourceId] : [],
    eventGroupLabel: association?.sourceType === 'course' ? 'Courses' : association?.sourceType === 'event' ? 'Events' : '',
    acceptanceLabel: '',
    versionLabel: '',
    active: true,
    sortOrder,
    _pendingFile: null,
  };
}

function sanitizeFileName(fileName = '') {
  return String(fileName || '')
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '') || 'document';
}

function hasMeaningfulDocumentInput(entry = {}) {
  return Boolean(
    String(entry?.title || '').trim()
    || String(entry?.description || '').trim()
    || String(entry?.externalUrl || '').trim()
    || String(entry?.acceptanceLabel || '').trim()
    || String(entry?.versionLabel || '').trim()
    || entry?._pendingFile
    || String(entry?.fileUrl || '').trim()
  );
}

function normalizeEditorDocument(doc = {}) {
  return {
    ...doc,
    _pendingFile: null,
  };
}

export default function EditDocumentsForm({ teamId, isOpen, onClose, onSave, defaultAssociation = null, dataAccess = null }) {
  const { deleteObject, getDownloadURL, uploadBytes } = dataAccess || { deleteObject: nativeDeleteObject, getDownloadURL: nativeGetDownloadURL, uploadBytes: nativeUploadBytes };
  const { getDocs, setDoc, deleteDoc } = dataAccess || { getDocs: nativeGetDocs, setDoc: nativeSetDoc, deleteDoc: nativeDeleteDoc };
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const initialDocumentsRef = useRef(new Map());
  const deletedDocIdsRef = useRef(new Set());

  useEffect(() => {
    if (!isOpen) return;

    async function loadDocuments() {
      setLoading(true);
      setError('');
      try {
        const snap = await getDocs(collection(db, 'teams', teamId, 'documents'));
        const loaded = normalizeTeamDocuments(snap.docs.map(docSnap => ({
          id: docSnap.id,
          ...docSnap.data(),
        })));
        initialDocumentsRef.current = new Map(loaded.map(item => [item.id, item]));
        deletedDocIdsRef.current = new Set();
        setDocuments(loaded.map(normalizeEditorDocument));
      } catch (err) {
        setError('Failed to load documents.');
      } finally {
        setLoading(false);
      }
    }

    loadDocuments();
  }, [isOpen, teamId, getDocs]);

  const handleAdd = () => {
    setDocuments(prev => [...prev, createEmptyDocument(prev.length, defaultAssociation)]);
  };

  const handleChange = (index, field, value) => {
    setDocuments(prev => prev.map((entry, entryIndex) => {
      if (entryIndex !== index) return entry;
      return {
        ...entry,
        [field]: value,
      };
    }));
  };

  const handleSourceTypeChange = (index, nextSourceType) => {
    setDocuments(prev => prev.map((entry, entryIndex) => {
      if (entryIndex !== index) return entry;
      if (nextSourceType === 'upload') {
        return {
          ...entry,
          sourceType: 'upload',
          externalUrl: '',
        };
      }
      return {
        ...entry,
        sourceType: 'external',
        _pendingFile: null,
      };
    }));
  };

  const handleFileChange = (index, file) => {
    setDocuments(prev => prev.map((entry, entryIndex) => {
      if (entryIndex !== index) return entry;
      return {
        ...entry,
        sourceType: 'upload',
        externalUrl: '',
        _pendingFile: file || null,
        fileName: file?.name || entry.fileName || '',
      };
    }));
  };

  const handleRemove = (index) => {
    const target = documents[index];
    if (!window.confirm(
      target?.id && initialDocumentsRef.current.has(target.id)
        ? `Remove "${target.title || 'this document'}"? Its saved file will be permanently deleted when you save.`
        : 'Discard this unsaved document?'
    )) return;
    setDocuments(prev => {
      const currentTarget = prev[index];
      if (currentTarget?.id && initialDocumentsRef.current.has(currentTarget.id)) {
        deletedDocIdsRef.current.add(currentTarget.id);
      }
      return prev.filter((_, entryIndex) => entryIndex !== index);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    const storageCleanupPaths = new Set();

    try {
      const existingDocuments = initialDocumentsRef.current;
      const docsToPersist = [];

      documents.forEach((entry, index) => {
        const normalized = {
          ...entry,
          title: String(entry?.title || '').trim(),
          description: String(entry?.description || '').trim(),
          externalUrl: String(entry?.externalUrl || '').trim(),
          fileName: String(entry?.fileName || '').trim(),
          acceptanceLabel: String(entry?.acceptanceLabel || '').trim(),
          versionLabel: String(entry?.versionLabel || '').trim(),
          sortOrder: Number.isFinite(Number(entry?.sortOrder)) ? Number(entry.sortOrder) : index,
        };

        if (!hasMeaningfulDocumentInput(normalized)) return;
        if (!normalized.title) {
          throw new Error('Each document needs a title.');
        }

        docsToPersist.push(normalized);
      });

      for (const entry of docsToPersist) {
        const existing = existingDocuments.get(entry.id) || null;
        const payload = {
          id: entry.id,
          title: entry.title,
          description: entry.description,
          sourceType: entry.sourceType === 'upload' ? 'upload' : 'external',
          fileUrl: '',
          externalUrl: '',
          fileName: '',
          storagePath: '',
          category: DOCUMENT_CATEGORIES.includes(entry.category) ? entry.category : 'reference',
          visibility: DOCUMENT_VISIBILITIES.includes(entry.visibility) ? entry.visibility : 'public',
          linkedEventIds: Array.isArray(entry.linkedEventIds) ? entry.linkedEventIds : [],
          linkedCourseIds: Array.isArray(entry.linkedCourseIds) ? entry.linkedCourseIds : [],
          eventGroupLabel: String(entry.eventGroupLabel || '').trim(),
          acceptanceLabel: entry.acceptanceLabel,
          versionLabel: entry.versionLabel,
          active: entry.active !== false,
          sortOrder: entry.sortOrder,
          updatedAt: serverTimestamp(),
        };

        if (payload.sourceType === 'external') {
          if (!entry.externalUrl) {
            throw new Error(`Add a URL for "${entry.title}".`);
          }
          payload.externalUrl = entry.externalUrl;
          if (existing?.sourceType === 'upload' && existing.storagePath) {
            storageCleanupPaths.add(existing.storagePath);
          }
        } else {
          if (entry._pendingFile) {
            const safeName = sanitizeFileName(entry._pendingFile.name);
            const associationGroup = defaultAssociation?.sourceId
              ? `${defaultAssociation.sourceType === 'course' ? 'courses' : 'events'}/${sanitizeFileName(defaultAssociation.sourceId)}/`
              : '';
            const storagePath = `team-documents/${teamId}/${associationGroup}${entry.id}/${Date.now()}-${safeName}`;
            const fileRef = ref(storage, storagePath);
            await uploadBytes(fileRef, entry._pendingFile, {
              contentType: entry._pendingFile.type || 'application/octet-stream',
              cacheControl: 'private, max-age=0, no-cache',
            });
            payload.fileUrl = payload.visibility === 'confirmed_bookers' ? '' : await getDownloadURL(fileRef);
            payload.fileName = entry._pendingFile.name || entry.fileName || safeName;
            payload.storagePath = storagePath;
            if (existing?.sourceType === 'upload' && existing.storagePath && existing.storagePath !== storagePath) {
              storageCleanupPaths.add(existing.storagePath);
            }
          } else if (existing?.sourceType === 'upload' && (existing.fileUrl || existing.storagePath)) {
            payload.fileUrl = existing.fileUrl;
            payload.fileName = existing.fileName || entry.fileName || '';
            payload.storagePath = existing.storagePath || '';
          } else {
            throw new Error(`Upload a file for "${entry.title}".`);
          }
          if (payload.visibility === 'confirmed_bookers' && payload.storagePath) {
            if (dataAccess) await dataAccess.protectFile(payload.storagePath);
            else await httpsCallable(functions, 'protectConfirmedDocumentFile')({ teamId, storagePath: payload.storagePath });
            payload.fileUrl = '';
          }
        }

        await setDoc(doc(db, 'teams', teamId, 'documents', entry.id), payload, { merge: true });
      }

      for (const deletedId of deletedDocIdsRef.current) {
        const existing = existingDocuments.get(deletedId);
        await deleteDoc(doc(db, 'teams', teamId, 'documents', deletedId));
        if (existing?.storagePath) {
          storageCleanupPaths.add(existing.storagePath);
        }
      }

      const hasRequiredJoinDocuments = docsToPersist.some(entry =>
        entry.active !== false && entry.category === 'join_requirement'
      );
      if (hasRequiredJoinDocuments) {
        await setDoc(doc(db, 'teams', teamId, 'joinFormConfig', 'config'), {
          enabled: true,
        }, {
          merge: true,
        });
      }

      for (const storagePath of storageCleanupPaths) {
        try {
          await deleteObject(ref(storage, storagePath));
        } catch (cleanupErr) {
          // Ignore missing-file cleanup failures; metadata write is the source of truth.
        }
      }

      onSave?.();
      onClose?.();
    } catch (err) {
      setError(err?.message || 'Failed to save documents.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content modal-content--xl" onClick={e => e.stopPropagation()}>
        <h2 className="modal-title">Edit Documents</h2>
        {error && <ErrorMessage message={error} />}

        {loading ? (
          <p>Loading...</p>
        ) : (
          <form className="modal-form" onSubmit={handleSubmit}>
            <p className="text-muted" style={{ marginBottom: '1rem' }}>
              Add useful club documents here. Mark any document as required on join to show it in the join flow.
            </p>

            <div style={{ display: 'grid', gap: '1rem' }}>
              {documents.map((entry, index) => {
                const existingUploadName = entry.sourceType === 'upload' && entry.fileName && !entry._pendingFile
                  ? entry.fileName
                  : '';

                return (
                  <div
                    key={entry.id}
                    style={{
                      border: '1px solid rgba(15, 23, 42, 0.1)',
                      borderRadius: '16px',
                      padding: '1rem',
                      background: 'rgba(248, 250, 252, 0.7)',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '1rem',
                        alignItems: 'center',
                        marginBottom: '0.75rem',
                      }}
                    >
                      <strong>Document {index + 1}</strong>
                      <button type="button" className="remove-btn" onClick={() => handleRemove(index)}>
                        Remove
                      </button>
                    </div>

                    <div style={{ display: 'grid', gap: '0.75rem' }}>
                      <div className="form-group">
                        <label>Title</label>
                        <input
                          className="form-input"
                          value={entry.title}
                          onChange={e => handleChange(index, 'title', e.target.value)}
                          placeholder="Club handbook"
                        />
                      </div>

                      <div className="form-group">
                        <label>Description</label>
                        <textarea
                          className="form-input"
                          rows={3}
                          value={entry.description}
                          onChange={e => handleChange(index, 'description', e.target.value)}
                          placeholder="Explain when members should read this."
                        />
                      </div>

                      <div
                        style={{
                          display: 'grid',
                          gap: '0.75rem',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))',
                        }}
                      >
                        <div className="form-group">
                          <label>Source</label>
                          <select
                            className="form-input"
                            value={entry.sourceType}
                            onChange={e => handleSourceTypeChange(index, e.target.value)}
                          >
                            <option value="external">External link</option>
                            <option value="upload">Upload file</option>
                          </select>
                        </div>

                        <div className="form-group">
                          <label>Category</label>
                          <select
                            className="form-input"
                            value={entry.category}
                            onChange={e => handleChange(index, 'category', e.target.value)}
                          >
                            <option value="reference">Reference</option>
                            <option value="join_requirement">Required on join</option>
                          </select>
                        </div>

                        <div className="form-group">
                          <label>Visibility</label>
                          <select
                            className="form-input"
                            value={entry.visibility}
                            onChange={e => handleChange(index, 'visibility', e.target.value)}
                          >
                            <option value="public">Public</option>
                            <option value="members">Members only</option>
                            <option value="confirmed_bookers">Confirmed bookers only</option>
                          </select>
                        </div>


                        <div className="form-group">
                          <label>Sort order</label>
                          <input
                            type="number"
                            className="form-input"
                            value={entry.sortOrder}
                            onChange={e => handleChange(index, 'sortOrder', e.target.value)}
                          />
                        </div>
                      </div>

                      {entry.sourceType === 'upload' ? (
                        <div className="form-group">
                          <label>File</label>
                          <input
                            type="file"
                            onChange={e => handleFileChange(index, e.target.files?.[0] || null)}
                          />
                          {entry._pendingFile && (
                            <p className="text-muted" style={{ marginTop: '0.35rem' }}>
                              Selected: {entry._pendingFile.name}
                            </p>
                          )}
                          {!entry._pendingFile && existingUploadName && (
                            <p className="text-muted" style={{ marginTop: '0.35rem' }}>
                              Current file: {existingUploadName}
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="form-group">
                          <label>External URL</label>
                          <input
                            type="url"
                            className="form-input"
                            value={entry.externalUrl}
                            onChange={e => handleChange(index, 'externalUrl', e.target.value)}
                            placeholder="https://example.com/club-handbook.pdf"
                          />
                        </div>
                      )}

                      <div
                        style={{
                          display: 'grid',
                          gap: '0.75rem',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
                        }}
                      >
                        <div className="form-group">
                          <label>Version label</label>
                          <input
                            className="form-input"
                            value={entry.versionLabel}
                            onChange={e => handleChange(index, 'versionLabel', e.target.value)}
                            placeholder="2026 season"
                          />
                        </div>

                        <div className="form-group">
                          <label>Acceptance label</label>
                          <input
                            className="form-input"
                            value={entry.acceptanceLabel}
                            onChange={e => handleChange(index, 'acceptanceLabel', e.target.value)}
                            placeholder="I agree to the club code of conduct."
                          />
                        </div>
                      </div>

                      <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <input
                          type="checkbox"
                          checked={entry.active !== false}
                          onChange={e => handleChange(index, 'active', e.target.checked)}
                        />
                        <span>Document is active</span>
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="form-actions" style={{ marginTop: '1.25rem' }}>
              <div className="form-actions-left">
                <SubmitButton
                  type="button"
                  label="Add Document"
                  onClick={handleAdd}
                  variant="secondary"
                />
              </div>
              <div className="form-actions-right">
                <SubmitButton
                  type="button"
                  label="Cancel"
                  onClick={onClose}
                  variant="ghost"
                  size="sm"
                  disabled={saving}
                />
                <SubmitButton
                  type="submit"
                  label={saving ? 'Saving…' : 'Save Documents'}
                  variant="primary"
                  disabled={saving}
                />
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
